import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/hooks/useTheme';

interface StepPrimaryContactProps {
  data: Record<string, string>;
  onChange: (data: Record<string, string>) => void;
  errors: Record<string, string>;
}

export default function StepPrimaryContact({ data, onChange, errors }: StepPrimaryContactProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const placeholderColor = isDark ? '#6B6B70' : '#9CA3AF';

  const textFields = [
    { key: 'fullName', labelKey: 'ob_s3_fullName', phKey: 'ob_s3_fullName_ph', required: true, type: 'text' },
    { key: 'position', labelKey: 'ob_s3_position', phKey: 'ob_s3_position_ph', required: true, type: 'text' },
    { key: 'email', labelKey: 'ob_s3_email', phKey: 'ob_s3_email_ph', required: true, type: 'email' },
    { key: 'phone', labelKey: 'ob_s3_phone', phKey: 'ob_s3_phone_ph', required: true, type: 'text' },
  ];

  const contactTypeOptions = [
    { value: 'technical', labelKey: 'ob_s3_ct_technical' },
    { value: 'legal', labelKey: 'ob_s3_ct_legal' },
    { value: 'financial', labelKey: 'ob_s3_ct_financial' },
    { value: 'dispatch', labelKey: 'ob_s3_ct_dispatch' },
    { value: 'commercial', labelKey: 'ob_s3_ct_commercial' },
    { value: 'general', labelKey: 'ob_s3_ct_general' },
  ];

  const handleChange = (key: string, value: string) => { onChange({ ...data, [key]: value }); };
  const handleBlur = (key: string) => { setTouched((prev) => ({ ...prev, [key]: true })); };

  return (
    <div className="space-y-5">
      <div className="mb-6">
        <h2 className="text-xl font-semibold" style={{ color: textMain, fontFamily: 'Inter, sans-serif' }}>
          {t('ob_s3_title')}
        </h2>
        <p className="text-sm mt-1" style={{ color: textMuted }}>
          {t('ob_s3_subtitle')}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {textFields.map((field) => (
          <div key={field.key}>
            <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>
              {t(field.labelKey)}
              {field.required && <span style={{ color: 'var(--accent)' }}>*</span>}
            </label>
            <input
              type={field.type}
              value={data[field.key] ?? ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              onBlur={() => handleBlur(field.key)}
              placeholder={t(field.phKey)}
              className="w-full px-3 py-2.5 rounded-md text-sm border transition-colors outline-none"
              style={{
                borderColor: touched[field.key] && errors[field.key] ? 'var(--accent)' : inputBorder,
                background: inputBg,
                color: textMain,
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; }}
            />
            {touched[field.key] && errors[field.key] && (
              <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors[field.key]}</p>
            )}
          </div>
        ))}
        <div>
          <label className="block text-sm font-medium mb-1.5" style={{ color: textMain }}>
            {t('ob_s3_contactType')}<span style={{ color: 'var(--accent)' }}>*</span>
          </label>
          <select
            value={data.contactType ?? ''}
            onChange={(e) => handleChange('contactType', e.target.value)}
            onBlur={() => handleBlur('contactType')}
            className="w-full px-3 py-2.5 rounded-md text-sm border transition-colors outline-none cursor-pointer"
            style={{
              borderColor: touched.contactType && errors.contactType ? 'var(--accent)' : inputBorder,
              background: inputBg,
              color: data.contactType ? textMain : placeholderColor,
            }}
          >
            <option value="" disabled>{t('ob_s3_contactType_ph')}</option>
            {contactTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
            ))}
          </select>
          {touched.contactType && errors.contactType && (
            <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>{errors.contactType}</p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={data.isPrimary === 'true'}
              onChange={(e) => handleChange('isPrimary', e.target.checked ? 'true' : 'false')}
              className="w-4 h-4 rounded border cursor-pointer"
              style={{ accentColor: 'var(--accent)' }}
            />
            <span className="text-sm" style={{ color: textMain }}>
              {t('ob_s3_isPrimary')}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
}